import React from 'react';
import styled from 'styled-components';

import { closeModal, initModal } from '../../../components/modal';
import {
  IdentificationListProps,
  IdentificationList,
  Card as CardForSharedDetails,
  SharedDetails
} from '../../details-modal';

import { useCardQuery, Card as CardFromQuery, CardIdentifier, useShouldHideCardNumber } from '../..';
import Button from '../../../components/button';
import { CardQueryErrorComponent } from '../..';
import LoadingSpinner from '../../../components/loading-spinner';
import { Text } from '../../../components/typography';

export type Card = CardForSharedDetails &
  Pick<CardFromQuery, 'cardStatus'> & {
    player?: IdentificationListProps;
  };

export const showActive = ({
  cardDetails,
  cardIdentifier,
  onClose
}: {
  /** card details will be queried when not specified */
  cardDetails?: Card;
  cardIdentifier: CardIdentifier;
  onClose?(): void;
}) => {
  const close = () => {
    closeModal();
    if (onClose) onClose();
  };

  initModal({
    actions: [],
    content: cardDetails ? (
      <ModalContent card={cardDetails} {...{ close }} />
    ) : (
      <ModalContentWithQuery cardIdentifier={cardIdentifier} {...{ close }} />
    ),
    open: true,
    title: 'Rewards Card Details',
    type: 'warning',
    width: 'default'
  });
};

function ModalContentWithQuery({ cardIdentifier, close }: { cardIdentifier: CardIdentifier; close(): void }) {
  const { data, error, loading } = useCardQuery(cardIdentifier);

  if (loading) {
    return (
      <p className="qa-player-card-rewards-card-active-loading">
        <LoadingSpinner />
      </p>
    );
  } else if (error || !data || !data.card) {
    return <CardQueryErrorComponent {...{ close }} />;
  } else {
    return <ModalContent card={data.card} close={close} />;
  }
}

export type ModalContentProps = { card: Card; close(): void };

export function ModalContent({ card, close }: ModalContentProps) {
  const shouldHideCardNumber = useShouldHideCardNumber();
  return (
    <RewardsModal className="qa-player-card-rewards-card-active-modal">
      <SharedDetails {...card} />
      {!shouldHideCardNumber && (
        <SharedDetailsContent>
          <span>Status</span>
          <span className="qa-player-card-rewards-card-active-status">
            <Activity size="lg" weight="regular">
              {card.cardStatus === 'INACTIVE' ? 'Inactive' : 'Active'}
            </Activity>
          </span>
        </SharedDetailsContent>
      )}
      <RewardsModalHr />
      {card.player && <IdentificationList identifications={card.player.identifications} />}
      <CloseButton
        className="qa-player-card-rewards-card-details-active-modal-close-button"
        modifiers="secondaryRound"
        onClick={close}>
        Close
      </CloseButton>
    </RewardsModal>
  );
}

const SharedDetailsContent = styled.p`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 1.4rem 0;
  padding: 0;
  &.centered {
    justify-content: center;
  }
  span {
    &:nth-child(1) {
      display: flex;
      font-weight: 600;
      justify-content: flex-start;
      text-align: left;
    }
    &:nth-child(2) {
      display: flex;
      justify-content: flex-end;
      text-align: right;
    }
  }
  &.identification {
    span {
      &:nth-child(1) {
        width: 50%;
      }
      &:nth-child(2) {
        width: 26%;
      }
      &:nth-child(3) {
        width: 10%;
        text-align: right;
      }
      &:nth-child(4) {
        text-align: right;
        width: 14%;
      }
    }
  }
`;

const RewardsModalHr = styled.hr`
  border: 0;
  height: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
`;

const RewardsModal = styled.div`
  width: 100%;
`;

const Activity = styled(Text)`
  background-color: ${({ theme }) => theme.colors.status.success};
  border-radius: ${({ theme }) => theme.borderRadius.xxlarge};
  display: inline-block;
  margin-left: 1rem;
  padding: 0.6rem 1.5rem;
`;

const CloseButton = styled(Button)`
  margin: 2rem auto 0 auto;
`;
